import { useCallback } from 'react'

import { errorMessage } from '@/api/client'
import type { AccessUser } from '@/api/types'
import { ICONS } from '@/components/icons/icons'
import { Modal } from '@/components/ui/Modal'

import styles from './Access.module.css'
import { useRemoveUser } from './useAccess'

interface RemoveAccessDialogProps {
  user: AccessUser
  onClose: () => void
  onRemoved: (user: AccessUser) => void
}

/** Confirm before removing someone's FPA access (rendered through the Modal portal). */
export function RemoveAccessDialog({ user, onClose, onRemoved }: RemoveAccessDialogProps) {
  const remove = useRemoveUser()
  const { mutate } = remove

  const handleConfirm = useCallback(() => {
    mutate(user.id, { onSuccess: () => onRemoved(user) })
  }, [mutate, onRemoved, user])

  return (
    <Modal title="Remove access?" subtitle={user.email} onClose={onClose} size="sm">
      <div className={styles.form}>
        <p className={styles.dialogText}>
          <strong>{user.name}</strong> will lose access to FP&amp;A immediately, including any open session. Their
          history stays, and you can add them again later.
        </p>
        {remove.error ? (
          <div className={styles.dialogError} role="alert">
            <ICONS.alert size={14} className={styles.dialogErrorIcon} />
            {errorMessage(remove.error, 'Could not remove access. Try again.')}
          </div>
        ) : null}
        <div className={styles.footer}>
          <button type="button" className={styles.secondary} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.danger}
            onClick={handleConfirm}
            disabled={remove.isPending}
            data-autofocus
          >
            <ICONS.trash size={14} />
            {remove.isPending ? 'Removing…' : 'Remove access'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
